import React from "react";
import { Typography, Grid } from "@mui/material";
import RenderSvg from "../renderSvg/RenderSvg";
import { useLocation } from "react-router-dom";
import { determineOrderType } from "../dms_common/useQuery";
import { OrderType } from "../../types/types";
import "./OrderSummary.css";
import { useSelector } from "react-redux";
import { truncateToTwoDecimals } from "../dms_common/DmsUtils";

interface OrderSummaryProps {
  outletCode: string;
  outletName: string;
  billDate: string;
  billToAddress: string;
  deliveryDate: string;
  shipToAddress: string;
  salesRep: string;
  salesType: string;
  customerChannel: string;
  paymentMethod: string;
  couponDiscount: number;
  schemeDiscount: number;
  totalAmount: number;
  finalAmount: number;
  taxAmount: number;
}

const OrderSummary = ({
  outletCode,
  outletName,
  billDate,
  billToAddress,
  deliveryDate,
  shipToAddress,
  salesRep,
  salesType,
  customerChannel,
  paymentMethod,
  couponDiscount,
  schemeDiscount,
  totalAmount,
  finalAmount,
  taxAmount,
}: OrderSummaryProps) => {
  const location = useLocation();
  const orderType = determineOrderType(location?.pathname);
  const isSecondaryOrder = orderType === OrderType.SECONDARY;

  const { createNewOrderState } = useSelector(
    (state: any) => state.secondaryOrder
  );
  const ConfigDictionary = JSON.parse(
    localStorage.getItem("ConfigDictionary") ?? "{}"
  );
  const { currency,  } = ConfigDictionary;

  const {
    shippingAddressMandatory,
  } = ConfigDictionary;
  const customShipToAddrStyling =
    shippingAddressMandatory && !shipToAddress
      ? { color: "#889096 !important", fontStyle: "italic" }
      : {};

  const updatedShipToAddress =
    shipToAddress ||
    (shippingAddressMandatory
      ? "No ship to address available"
      : "");

  return (
    <div className="order_summary_container">
      <div className="sub_header">
        <div className="confirmed_order_icon">
          <RenderSvg name="orderConfirmedIcon" />
        </div>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: "16px !important",
            color: "#227C9D",
          }}
        >
          Order Summary
        </Typography>
      </div>
      <Grid className="grid-container">
        {isSecondaryOrder && (
          <Grid item xs={12} className="checkout_summary_label_value_container">
            <Typography className="label">Customer</Typography>
            <Typography className="value">{`${outletCode} - ${outletName}`}</Typography>
          </Grid>
        )}
        {isSecondaryOrder && (
          <Grid item xs={12} className="checkout_summary_label_value_container">
            <Typography className="label">Sales Rep</Typography>
            <Typography className="value">{salesRep}</Typography>
          </Grid>
        )}
        {isSecondaryOrder && (
          <Grid item xs={12} className="checkout_summary_label_value_container">
            <Typography className="label">Sales Type</Typography>
            <Typography className="value">{salesType}</Typography>
          </Grid>
        )}
        {isSecondaryOrder && (
          <Grid item xs={12} className="checkout_summary_label_value_container">
            <Typography className="label">Sales Channel</Typography>
            <Typography className="value">{customerChannel}</Typography>
          </Grid>
        )}
        <Grid item xs={12} className="checkout_summary_label_value_container">
          <Typography className="label">Address</Typography>
          <Typography className="value">{billToAddress}</Typography>
        </Grid>
        <Grid item xs={12} className="checkout_summary_label_value_container">
          <Typography className="label">Bill Date</Typography>
          <Typography className="value">{billDate}</Typography>
        </Grid>
        {orderType === OrderType.SECONDARY && (
        <Grid item xs={12} className="checkout_summary_label_value_container">
          <Typography className="label">Delivery Date</Typography>
          <Typography className="value">{deliveryDate}</Typography>
        </Grid>
        )}
        {orderType === OrderType.PRIMARY && (
        <Grid item xs={12} className="checkout_summary_label_value_container">
          <Typography className="label">Est. Delivery Date</Typography>
          <Typography className="value">{deliveryDate}</Typography>
        </Grid>
        )}
        <Grid item xs={12} className="checkout_summary_label_value_container">
          <Typography className="label">Payment Method</Typography>
          <Typography className="value">{paymentMethod}</Typography>
        </Grid>
        <Grid item xs={12} className="checkout_summary_label_value_container">
          <Typography className="label">Total Amount (excl. Tax)</Typography>
          <Typography className="value">
            {currency}{" "}
            {truncateToTwoDecimals(totalAmount)}
          </Typography>
        </Grid>
        {schemeDiscount > 0 && (
          <Grid item xs={12} className="checkout_summary_label_value_container">
            <Typography className="label">Scheme Discount</Typography>
            <Typography className="value">
              {currency}{" "}
              {truncateToTwoDecimals(schemeDiscount)}
            </Typography>
          </Grid>
        )}
        {couponDiscount > 0 && (
          <Grid item xs={12} className="checkout_summary_label_value_container">
            <Typography className="label">Coupon Discount</Typography>
            <Typography className="value">
              {currency}{" "}
              {truncateToTwoDecimals(couponDiscount)}
            </Typography>
          </Grid>
        )}
        {taxAmount > 0 && (
          <Grid item xs={12} className="checkout_summary_label_value_container">
            <Typography className="label">Total Tax</Typography>
            <Typography className="value">
              {currency}{" "}
              {truncateToTwoDecimals(taxAmount)}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} className="checkout_summary_label_value_container">
          <Typography className="label">Final Bill Value</Typography>
          <Typography className="value">
            {currency}{" "}
            {truncateToTwoDecimals(finalAmount)}
          </Typography>
        </Grid>
        {updatedShipToAddress &&
          (createNewOrderState?.diffShippingAddress ||
            shippingAddressMandatory) && (
            <Grid
              item
              xs={12}
              className="checkout_summary_label_value_container"
            >
              <Typography className="label">Ship to address</Typography>
              <Typography className="value" sx={{ ...customShipToAddrStyling }}>
                {updatedShipToAddress}
              </Typography>
            </Grid>
          )}
      </Grid>
    </div>
  );
};

export default OrderSummary;
